/*
学習パスの項目が、クラウドエディターの章のどの道で済むか。api-go の cloudeditor.CompletionAllowed と同じ決まりで、
書き出し (guides-cli.ts の itemGuidesGo) と学習パスの画面の「次の学習パスへ」(path-cloudeditor.ts) がここを読む。

  read     本文を読み終えた印 (板の「読み終えた」)
  checks   機械の確かめが全部通った (項目の章の手順、または項目がつなぐレッスンの章の全部)
  handin   提出した (宿題。2026-10-20 の代表の決定で講師への提出を外したので、今はこの道で済む項目は無い)
  quiz     クイズに合格した (今の確認テストの採点)
*/
import { LESSON_GUIDE_INDEX, LESSON_GUIDE_PATH_ITEMS, LESSON_GUIDE_PATH_OUTSIDE } from "./lesson-guides.generated";

/** 学習パスの項目の種類 (team_curriculums.item_type) */
export type PathItemType = "task" | "course" | "article" | "milestone" | "homework" | "quiz";

export type ItemCompletion = "read" | "checks" | "handin" | "quiz";

/** 項目の種類が、その道で済んでよいか。api-go の CompletionAllowed と同じ決まり (item-completion.test.ts と item_guides_test.go が同じ表で試す) */
export function completionAllowed(itemType: string | null | undefined, path: ItemCompletion): boolean {
  switch ((itemType ?? "").trim().toLowerCase()) {
    case "task":
    case "course":
    case "article":
    case "milestone":
      return path === "read" || path === "checks";
    case "homework":
      return path === "handin";
    case "quiz":
      return path === "quiz";
  }
  return false;
}

/**
 * 項目の本文を写した章 (itemId を持つ章) の道。書いてあればそれ、無ければ読むだけの章 (readOnly) と、
 * 読んでから端末で試す章 (envFrom。試す手順に機械の確かめが無い) は read、それ以外は checks。
 * 試す手順に確かめを足した章は、completion: "checks" を書く (書き出しが、確かめのある read の章を断る)
 */
export function itemGuideCompletion(guide: { completion?: ItemCompletion; readOnly?: true; envFrom?: string }): ItemCompletion {
  return guide.completion ?? (guide.readOnly || guide.envFrom ? "read" : "checks");
}

/**
 * レッスンの章をつないだ項目と、項目がつなぐレッスン (lessons.id) の並び。api-go は、ここにある項目のうち、今の DB の必須のレッスン (required_lessons.go) が全部済んだ項目を済ませる。
 * ここの並びは、どのレッスンの章の進みから項目を見直すかの道しるべで、完了の判定には使わない。
 * 載せるのは、項目の中の章 (LESSON_GUIDE_PATH_ITEMS) が全部レッスンを写した章で、エディターの外で解くレッスン
 * (LESSON_GUIDE_PATH_OUTSIDE。quiz) が残っていない項目だけ。外で解くレッスンが残る項目は、クイズの手順 (T2) が入るまで載せない
 */
export function lessonItems(
  pathItems: Readonly<Record<string, readonly string[]>> = LESSON_GUIDE_PATH_ITEMS,
  outside: Readonly<Record<string, number>> = LESSON_GUIDE_PATH_OUTSIDE,
  index: Readonly<Record<string, { lessonId?: string }>> = LESSON_GUIDE_INDEX,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const [item, guides] of Object.entries(pathItems)) {
    if ((outside[item] ?? 0) > 0 || guides.length === 0) continue;
    const lessons = guides.map((id) => (Object.hasOwn(index, id) ? index[id].lessonId : undefined));
    if (lessons.some((lesson) => lesson === undefined)) continue;
    out[item] = [...new Set(lessons as string[])];
  }
  return out;
}
